import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';

const ETIQUETAS_TIPO = {
  pagos_sin_etiquetar: 'Pagos sin etiquetar',
  pagos_huerfanos: 'Pagos sin préstamo',
  valores_invalidos: 'Valores inválidos',
  prestamos_activos_simultaneos: 'Préstamos activos a la vez',
  finalizado_sin_terminar_de_pagar: 'Finalizado sin terminar de pagar',
  deberia_estar_finalizado: 'Debería estar finalizado',
  solo_debe_interes: 'Solo debe interés',
  sobrepago: 'Sobrepago de capital',
  solicitud_estancada: 'Solicitud estancada',
  pago_antes_de_aprobacion_formal: 'Pago antes de aprobación formal'
};

const COLOR_SEVERIDAD = { alta: 'danger', media: 'warning', baja: 'secondary' };

const SaludPrestamos = () => {
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroSeveridad, setFiltroSeveridad] = useState('todas');

  const ejecutarAuditoria = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/salud-prestamos');
      setResultado(response.data);
    } catch (error) {
      console.error('Error al ejecutar la auditoría:', error);
      toast.error('Error al ejecutar la auditoría de préstamos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    ejecutarAuditoria();
  }, [ejecutarAuditoria]);

  const hallazgosFiltrados = (resultado?.data || []).filter(h =>
    filtroSeveridad === 'todas' || h.severidad === filtroSeveridad
  );

  return (
    <Container fluid className="mt-4">
      <div className="fade-in">
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <h2 className="text-gradient mb-2">
                      <i className="fas fa-heartbeat me-2"></i>
                      Salud de Préstamos
                    </h2>
                    <p className="text-muted mb-0">
                      Chequeos automáticos que detectan datos de préstamos raros ANTES de que un socio se queje
                      (pagos mezclados entre préstamos, saldos que no cuadran, préstamos aprobados antes de
                      terminar uno anterior, etc.)
                    </p>
                  </div>
                  <Button variant="primary" onClick={ejecutarAuditoria} disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : <><i className="fas fa-sync me-2"></i>Volver a revisar</>}
                  </Button>
                </div>
                {resultado?.ejecutadoEn && (
                  <small className="text-muted d-block mt-2">
                    Última revisión: {new Date(resultado.ejecutadoEn).toLocaleString('es-ES')}
                  </small>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {loading && !resultado ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <>
            <Row className="mb-4">
              <Col md={3}>
                <Card
                  className={`stats-card border-danger ${filtroSeveridad === 'alta' ? 'shadow' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setFiltroSeveridad(filtroSeveridad === 'alta' ? 'todas' : 'alta')}
                >
                  <Card.Body>
                    <div className="stats-icon text-danger"><i className="fas fa-exclamation-circle"></i></div>
                    <div className="stats-number text-danger">{resultado?.porSeveridad?.alta || 0}</div>
                    <div className="stats-label">Alta — revisar ya</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className={`stats-card border-warning ${filtroSeveridad === 'media' ? 'shadow' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setFiltroSeveridad(filtroSeveridad === 'media' ? 'todas' : 'media')}
                >
                  <Card.Body>
                    <div className="stats-icon text-warning"><i className="fas fa-exclamation-triangle"></i></div>
                    <div className="stats-number text-warning">{resultado?.porSeveridad?.media || 0}</div>
                    <div className="stats-label">Media — conviene revisar</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className={`stats-card border-secondary ${filtroSeveridad === 'baja' ? 'shadow' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setFiltroSeveridad(filtroSeveridad === 'baja' ? 'todas' : 'baja')}
                >
                  <Card.Body>
                    <div className="stats-icon text-secondary"><i className="fas fa-info-circle"></i></div>
                    <div className="stats-number text-secondary">{resultado?.porSeveridad?.baja || 0}</div>
                    <div className="stats-label">Baja — informativo</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="stats-card border-success">
                  <Card.Body>
                    <div className="stats-icon text-success"><i className="fas fa-check-circle"></i></div>
                    <div className="stats-number text-success">{resultado?.total === 0 ? '✓' : (resultado?.total || 0)}</div>
                    <div className="stats-label">Total hallazgos</div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {resultado?.total === 0 ? (
              <Alert variant="success">
                <i className="fas fa-check-circle me-2"></i>
                No se encontró ningún problema. Todos los préstamos con más de un registro cuadran correctamente.
              </Alert>
            ) : (
              <Row>
                <Col>
                  <Card className="card-custom">
                    <Card.Header className="card-header-custom d-flex justify-content-between align-items-center">
                      <h5 className="mb-0"><i className="fas fa-list me-2"></i>Hallazgos</h5>
                      {filtroSeveridad !== 'todas' && (
                        <Button variant="outline-secondary" size="sm" onClick={() => setFiltroSeveridad('todas')}>
                          Quitar filtro
                        </Button>
                      )}
                    </Card.Header>
                    <Card.Body>
                      <div className="table-responsive">
                        <Table hover>
                          <thead>
                            <tr>
                              <th>Severidad</th>
                              <th>Tipo</th>
                              <th>Socio</th>
                              <th>Préstamo</th>
                              <th>Detalle</th>
                            </tr>
                          </thead>
                          <tbody>
                            {hallazgosFiltrados.map((h, idx) => (
                              <tr key={idx}>
                                <td>
                                  <Badge bg={COLOR_SEVERIDAD[h.severidad] || 'secondary'}>
                                    {h.severidad.toUpperCase()}
                                  </Badge>
                                </td>
                                <td><small>{ETIQUETAS_TIPO[h.tipo] || h.tipo}</small></td>
                                <td className="fw-bold">{h.socio}</td>
                                <td>{h.prestamo || '—'}</td>
                                <td>
                                  {h.detalle}
                                  {h.meses && h.meses.length > 0 && (
                                    <div><small className="text-muted">Meses: {h.meses.join(', ')}</small></div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </>
        )}
      </div>
    </Container>
  );
};

export default SaludPrestamos;
